import { Node } from '@xyflow/react';
import { IVlanItem, IVlanItemWithVariable } from 'src/context/global/types';
import { IVariableInfo, IVariableOptions } from 'src/context/wizard/types';
import { NodeCategoryModel } from 'src/modules/network-configuration/models/node-data';

// Regex to check if the value contains a variable
export const variableRegex = /\{[a-zA-Z0-9]+\}/;
export const hasVariable = (value: string): boolean =>
  variableRegex.test(value);

export const extractVariables = (str: string): string[] => {
  const regex = /\{([^}]+)\}/g;
  const result = [];

  let match = regex.exec(str);
  while (match !== null) {
    result.push(match[1]);
    match = regex.exec(str);
  }

  return result;
};

// Helper function to replace variables in a string
export const replaceVariable = (
  str: string,
  variableName: string,
  value: string,
): string => {
  const regex = new RegExp(`\\{${variableName}\\}`, 'g');
  return str.replace(regex, value);
};

const getNodeVariableInfo = (nodes: Node[]): string[] => {
  const variables: string[] = [];
  nodes.forEach((node) => {
    const categoryData = node.data?.category as NodeCategoryModel;
    if (categoryData.ip && hasVariable(categoryData.ip)) {
      variables.push(...extractVariables(categoryData.ip));
    }
    if (categoryData.subnetMask && hasVariable(categoryData.subnetMask)) {
      variables.push(...extractVariables(categoryData.subnetMask));
    }
    // ipInterfaces
    if (categoryData.ipInterfaces) {
      categoryData.ipInterfaces.forEach((ipInterface) => {
        if (ipInterface.ip && hasVariable(ipInterface.ip)) {
          variables.push(...extractVariables(ipInterface.ip));
        }
        if (ipInterface.subnetMask && hasVariable(ipInterface.subnetMask)) {
          variables.push(...extractVariables(ipInterface.subnetMask));
        }
      });
    }
  });
  return variables;
};

const getVlanVariableInfo = (vlans: IVlanItemWithVariable[]): string[] => {
  const variables: string[] = [];
  vlans.forEach((vlan) => {
    if (hasVariable(vlan.vlanId)) {
      variables.push(...extractVariables(vlan.vlanId));
    }
    if (hasVariable(vlan.description)) {
      variables.push(...extractVariables(vlan.description));
    }
  });
  return variables;
};

export const getVariableInfo = (
  vlans: IVlanItemWithVariable[],
  switches: Node[],
  devices: Node[],
): IVariableInfo => {
  const vlanVariables = getVlanVariableInfo(vlans);
  const switchVariables = getNodeVariableInfo(switches);
  const deviceVariables = getNodeVariableInfo(devices);

  // Unique names only — same name reused in multiple fields shares one option/value
  const uniqueVariables = [
    ...new Set([...vlanVariables, ...switchVariables, ...deviceVariables]),
  ].sort();

  return {
    items: uniqueVariables,
    vlanVariables: [...new Set(vlanVariables)].sort(),
    switchVariables: [...new Set(switchVariables)].sort(),
    deviceVariables: [...new Set(deviceVariables)].sort(),
  };
};

// Helper function to get variable value for a specific file index
export const getVariableValue = (
  currentVariableOptions: Record<string, IVariableOptions>,
  variableName: string,
  fileIndex: number,
): string => {
  const option = currentVariableOptions[variableName];
  if (!option) return '';

  if (option.type === 'range') {
    return String(option.from + fileIndex);
  }
  return option.list[fileIndex] || '';
};

// Helper function to apply variables to a string
export const applyVariablesToString = (
  currentVariableOptions: Record<string, IVariableOptions>,
  str: string,
  fileIndex: number,
): string => {
  let result = str;
  const variables = extractVariables(str);
  variables.forEach((variableName) => {
    const value = getVariableValue(
      currentVariableOptions,
      variableName,
      fileIndex,
    );
    result = replaceVariable(result, variableName, value);
  });
  return result;
};

// Helper function to apply variables to vlans
export const applyVariablesToVlans = (
  currentVariableOptions: Record<string, IVariableOptions>,
  vlanList: IVlanItemWithVariable[],
  fileIndex: number,
): IVlanItem[] =>
  vlanList.map((vlan) => ({
    ...vlan,
    vlanId: parseInt(
      applyVariablesToString(currentVariableOptions, vlan.vlanId, fileIndex),
      10,
    ),
    description: applyVariablesToString(
      currentVariableOptions,
      vlan.description,
      fileIndex,
    ),
    ipSubnet: vlan.ipSubnet
      ? applyVariablesToString(currentVariableOptions, vlan.ipSubnet, fileIndex)
      : undefined,
    ip: vlan.ip
      ? applyVariablesToString(currentVariableOptions, vlan.ip, fileIndex)
      : undefined,
    subnetMask: vlan.subnetMask
      ? applyVariablesToString(
          currentVariableOptions,
          vlan.subnetMask,
          fileIndex,
        )
      : undefined,
  }));

// Helper function to apply variables to nodes
export const applyVariablesToNodes = (
  currentVariableOptions: Record<string, IVariableOptions>,
  nodeList: Node[],
  fileIndex: number,
): Node[] =>
  nodeList.map((node) => {
    const category = node.data?.category as NodeCategoryModel;
    if (!category) return node;

    const updatedCategory: NodeCategoryModel = {
      ...category,
      ip: category.ip
        ? applyVariablesToString(currentVariableOptions, category.ip, fileIndex)
        : category.ip,
      subnetMask: category.subnetMask
        ? applyVariablesToString(
            currentVariableOptions,
            category.subnetMask,
            fileIndex,
          )
        : category.subnetMask,
      ipInterfaces: category.ipInterfaces
        ? category.ipInterfaces.map((ipInterface) => ({
            ...ipInterface,
            ip: ipInterface.ip
              ? applyVariablesToString(
                  currentVariableOptions,
                  ipInterface.ip,
                  fileIndex,
                )
              : ipInterface.ip,
            subnetMask: ipInterface.subnetMask
              ? applyVariablesToString(
                  currentVariableOptions,
                  ipInterface.subnetMask,
                  fileIndex,
                )
              : ipInterface.subnetMask,
          }))
        : category.ipInterfaces,
    };

    return {
      ...node,
      data: {
        ...node.data,
        category: updatedCategory,
      },
    };
  });

// Helper function to validate a single variable option
export const validateVariableOption = (
  option: IVariableOptions,
  numberOfFilesToExport: number,
): IVariableOptions => {
  const errors: string[] = [];

  if (option.type === 'range') {
    if (option.from > option.to) {
      errors.push('From must be less than or equal to to');
    }
    if (option.from < 1) {
      errors.push('From must be greater than 0');
    }
    if (option.to < 1) {
      errors.push('To must be greater than 0');
    }
    if (option.to - option.from + 1 !== numberOfFilesToExport) {
      errors.push('Range must equal to the number of files to export');
    }
  }

  if (option.type === 'list') {
    if (option.list.length !== numberOfFilesToExport) {
      errors.push('List length must equal to the number of files to export');
    }
    if (option.list.some((line) => line.trim() === '')) {
      errors.push('List must not contain empty items');
    }
  }

  return {
    ...option,
    errorMessage: errors.join('. '),
    status: errors.length === 0 ? ('success' as const) : ('error' as const),
  };
};
